import { Alert, Box, Stack, TextField, Typography } from "@mui/material"
import { useCallback, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import { peekPasswordSetup, completePasswordSetup } from "../../../data-utils/data"
import { ROUTE_PATHS, DEFAULT_LOGO } from "../../../utilities/constants.js"
import { appVersionString } from "../../../data-utils/uiUtils.js"
import { UiCtButton } from "../../common/MuiStyled/LibComponent.jsx"
import Loader from "../../common/Loader/loader.jsx"
import logger from "../../../Logger/index.js"
import "./Login.scss"

const pageLogger = logger.child({ module: "set password" })

/**
 * Set-password landing page for an invited administrator.
 *
 * WHY THE APP OWNS THIS PAGE (plan L4)
 * ------------------------------------
 * On Auth0 the invite email carries a password-change TICKET minted by the Management
 * API. Keycloak has no equivalent — its only option makes Keycloak send its own email,
 * which would split identity email across two systems and lose the Postmark branding.
 * So the server mints a one-time link to this page instead, and the invite email stays
 * exactly the template it has always been.
 *
 * The token in `?token=` is single-use, TTL'd and HMAC-signed server-side; this page
 * never sees or handles the account id.
 */
export const SetPassword = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tokenId = searchParams.get("token")

  const [checking, setChecking] = useState(true)
  const [email, setEmail] = useState(null)
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let cancelled = false
    const check = async () => {
      if (!tokenId) {
        setChecking(false)
        return
      }
      const result = await peekPasswordSetup({ tokenId })
      if (cancelled) return
      setEmail(result?.valid ? result.email : null)
      setChecking(false)
    }
    check()
    return () => {
      cancelled = true
    }
  }, [tokenId])

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault()
      setError(null)

      // Checked here purely for immediate feedback. The authoritative rules are the
      // realm's password policy, enforced by Keycloak on write — so this deliberately
      // does NOT duplicate them and cannot drift out of sync.
      if (password !== confirm) {
        setError("The two passwords do not match.")
        return
      }

      setBusy(true)
      const result = await completePasswordSetup({ tokenId, password })
      setBusy(false)

      if (!result.ok) {
        setError(result.message)
        return
      }
      pageLogger.info("Password set; returning to sign-in")
      setDone(true)
    },
    [password, confirm, tokenId]
  )

  const panel = () => {
    if (checking) return <Loader enabled={true} />

    if (!email) {
      return (
        <Stack sx={{ gap: 2, maxWidth: 340, textAlign: "center" }}>
          <Typography variant="h2" sx={{ fontSize: 20, fontWeight: 600, color: "text.primary" }}>
            This link is no longer valid
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 14 }}>
            Links can only be used once. Invitation links expire after a week, password reset links after an hour. Use &ldquo;Forgot
            password?&rdquo; on the sign-in page for a new link, or ask your ChaosTrack administrator to send a new invitation.
          </Typography>
          <UiCtButton variant="contained" onClick={() => navigate(ROUTE_PATHS.ROOT)} sx={{ py: 1 }}>
            Back to sign in
          </UiCtButton>
        </Stack>
      )
    }

    if (done) {
      return (
        <Stack sx={{ gap: 2, maxWidth: 340, textAlign: "center" }}>
          <Typography variant="h2" sx={{ fontSize: 20, fontWeight: 600, color: "text.primary" }}>
            You&rsquo;re all set
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 14 }}>
            Your password has been saved. Sign in with <strong>{email}</strong> to get started.
          </Typography>
          <UiCtButton variant="contained" onClick={() => navigate(ROUTE_PATHS.ROOT)} sx={{ py: 1 }}>
            Go to sign in
          </UiCtButton>
        </Stack>
      )
    }

    return (
      <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", maxWidth: 340 }}>
        <Stack sx={{ gap: 2, alignItems: "stretch" }}>
          <Typography variant="h2" sx={{ fontSize: 20, fontWeight: 600, color: "text.primary", textAlign: "center" }}>
            Choose a password
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", fontSize: 14 }}>
            Setting up <strong>{email}</strong>
          </Typography>

          <TextField
            autoFocus
            fullWidth
            size="small"
            type="password"
            label="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{ autoComplete: "new-password", maxLength: 256 }}
            disabled={busy}
          />
          <TextField
            fullWidth
            size="small"
            type="password"
            label="Confirm password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            inputProps={{ autoComplete: "new-password", maxLength: 256 }}
            disabled={busy}
          />

          {error && (
            <Alert severity="error" sx={{ fontSize: 13, py: 0 }}>
              {error}
            </Alert>
          )}

          <UiCtButton type="submit" variant="contained" disabled={busy || !password || !confirm} sx={{ py: 1 }}>
            {busy ? "Saving…" : "Save password"}
          </UiCtButton>
        </Stack>
      </Box>
    )
  }

  return <LoginPanelShell>{panel()}</LoginPanelShell>
}

/** The branded two-column frame of the public sign-in pages (set password, forgot password). */
export const LoginPanelShell = ({ children }) => {
  const appVersion = appVersionString(`${window.__RUNTIME_CONFIG__.REACT_APP_VERSION ?? ""}`)
  return (
    <Stack className="section-login" sx={{ alignItems: "center", justifyContent: "center" }}>
      <div className="login-container">
        <div className="login-brand-wrapper">
          <Stack className="brand-container" sx={{ px: 1, height: "100%", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
            <img className="brand-logo" src={DEFAULT_LOGO} alt="brand logo" />
            <span className="icon-wrapper py-3">
              <i className="fa-solid fa-user-secret"></i>
            </span>
            <Typography
              variant="h1"
              sx={{
                color: "text.primary",
                padding: (theme) => theme.spacing(1, 2),
                fontSize: { sm: 20, md: 20, lg: 24 },
                fontWeight: 500,
              }}
            >
              Prepare, Respond, Prevent
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "text.tertiary", fontSize: 14 }}>
              {appVersion ?? ""}
            </Typography>
          </Stack>
        </div>
        <Box className="login-form-wrapper" sx={{ px: 1, backgroundColor: "background.level2" }}>
          <Box sx={{ display: "grid", placeItems: "center", height: "100%", color: "text.secondary" }}>{children}</Box>
        </Box>
      </div>
    </Stack>
  )
}

export default SetPassword
